/**
 * Component Data Context
 *
 * Manages all component data (CPU, GPU, Memory, etc.) loading and state
 * Separated from main AppContext for better performance and maintainability
 */

import React, { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react'
import type { CPUModel } from '../models/cpu/cpu-model'
import type { MotherboardListItem } from '../models/motherboard/motherboard-list-model'
import type { GPUModel } from '../models/gpu/gpu-model'
import type { MemoryModel } from '../models/memory/memory-model'
import type { StorageModel } from '../models/storage/storage-model'
import type { PSUModel } from '../models/psu/psu-model'
import type { CaseModel } from '../models/case/case-model'
import type { CoolerModel } from '../models/cooler/cpu-cooler-model'
import { getStorageDataAdapter } from '../lib/storage-data/StorageDataAdapter'
import { getCPUDataAdapter } from '../lib/cpu-data/CPUDataAdapter'
import { getMotherboardDataAdapter } from '../lib/motherboard-data/MotherboardDataAdapter'
import { getCaseDataAdapter } from '../lib/case-data/CaseDataAdapter'
import { getGPUDataAdapter } from '../lib/gpu-data/GPUDataAdapter'
import { getMemoryDataAdapter } from '../lib/memory-data/MemoryDataAdapter'
import { getPSUDataAdapter } from '../lib/psu-data/PSUDataAdapter'
import { getCoolerDataAdapter } from '../lib/cooler-data/CoolerDataAdapter'

/**
 * Component data state interface
 */
export interface ComponentDataState {
  // CPU Data
  cpus: CPUModel[]
  cpuLoading: boolean
  cpuError: string | null
  cpuDiscontinuedLoaded: boolean

  // GPU Data
  gpus: GPUModel[]
  gpuLoading: boolean
  gpuError: string | null
  gpuDiscontinuedLoaded: boolean

  // Motherboard Data
  motherboards: MotherboardListItem[]
  motherboardLoading: boolean
  motherboardError: string | null
  motherboardDiscontinuedLoaded: boolean

  // Memory Data
  memory: MemoryModel[]
  memoryLoading: boolean
  memoryError: string | null
  memoryDiscontinuedLoaded: boolean

  // Storage Data
  storage: StorageModel[]
  storageLoading: boolean
  storageError: string | null
  storageDiscontinuedLoaded: boolean

  // PSU Data
  psus: PSUModel[]
  psuLoading: boolean
  psuError: string | null
  psuDiscontinuedLoaded: boolean

  // Case Data
  cases: CaseModel[]
  caseLoading: boolean
  caseError: string | null
  caseDiscontinuedLoaded: boolean

  // Cooler Data
  coolers: CoolerModel[]
  coolerLoading: boolean
  coolerError: string | null
  coolerDiscontinuedLoaded: boolean

  // Cache
  lastUpdated: number | null
}

/**
 * Component data action types
 */
export type ComponentDataAction =
  // CPU Data Actions
  | { type: 'SET_CPU_LOADING'; payload: boolean }
  | { type: 'SET_CPU_ERROR'; payload: string | null }
  | { type: 'SET_CPUS'; payload: CPUModel[] }
  | { type: 'ADD_CPU'; payload: CPUModel }
  | { type: 'UPDATE_CPU'; payload: CPUModel }
  | { type: 'REMOVE_CPU'; payload: string }

  // GPU Data Actions
  | { type: 'SET_GPU_LOADING'; payload: boolean }
  | { type: 'SET_GPU_ERROR'; payload: string | null }
  | { type: 'SET_GPUS'; payload: GPUModel[] }
  | { type: 'ADD_GPU'; payload: GPUModel }
  | { type: 'UPDATE_GPU'; payload: GPUModel }
  | { type: 'REMOVE_GPU'; payload: string }

  // Motherboard Data Actions
  | { type: 'SET_MOTHERBOARD_LOADING'; payload: boolean }
  | { type: 'SET_MOTHERBOARD_ERROR'; payload: string | null }
  | { type: 'SET_MOTHERBOARDS'; payload: MotherboardListItem[] }
  | { type: 'ADD_MOTHERBOARD'; payload: MotherboardListItem }
  | { type: 'UPDATE_MOTHERBOARD'; payload: MotherboardListItem }
  | { type: 'REMOVE_MOTHERBOARD'; payload: string }

  // Memory Data Actions
  | { type: 'SET_MEMORY_LOADING'; payload: boolean }
  | { type: 'SET_MEMORY_ERROR'; payload: string | null }
  | { type: 'SET_MEMORY'; payload: MemoryModel[] }
  | { type: 'ADD_MEMORY'; payload: MemoryModel }
  | { type: 'UPDATE_MEMORY'; payload: MemoryModel }
  | { type: 'REMOVE_MEMORY'; payload: string }

  // Storage Data Actions
  | { type: 'SET_STORAGE_LOADING'; payload: boolean }
  | { type: 'SET_STORAGE_ERROR'; payload: string | null }
  | { type: 'SET_STORAGE'; payload: StorageModel[] }
  | { type: 'ADD_STORAGE'; payload: StorageModel }
  | { type: 'UPDATE_STORAGE'; payload: StorageModel }
  | { type: 'REMOVE_STORAGE'; payload: string }

  // PSU Data Actions
  | { type: 'SET_PSU_LOADING'; payload: boolean }
  | { type: 'SET_PSU_ERROR'; payload: string | null }
  | { type: 'SET_PSUS'; payload: PSUModel[] }
  | { type: 'ADD_PSU'; payload: PSUModel }
  | { type: 'UPDATE_PSU'; payload: PSUModel }
  | { type: 'REMOVE_PSU'; payload: string }

  // Case Data Actions
  | { type: 'SET_CASE_LOADING'; payload: boolean }
  | { type: 'SET_CASE_ERROR'; payload: string | null }
  | { type: 'SET_CASES'; payload: CaseModel[] }
  | { type: 'ADD_CASE'; payload: CaseModel }
  | { type: 'UPDATE_CASE'; payload: CaseModel }
  | { type: 'REMOVE_CASE'; payload: string }

  // Cooler Data Actions
  | { type: 'SET_COOLER_LOADING'; payload: boolean }
  | { type: 'SET_COOLER_ERROR'; payload: string | null }
  | { type: 'SET_COOLERS'; payload: CoolerModel[] }
  | { type: 'ADD_COOLER'; payload: CoolerModel }
  | { type: 'UPDATE_COOLER'; payload: CoolerModel }
  | { type: 'REMOVE_COOLER'; payload: string }

  // Cache Actions
  | { type: 'UPDATE_CACHE_TIMESTAMP' }

/**
 * Initial component data state
 */
const initialComponentDataState: ComponentDataState = {
  // CPU Data
  cpus: [],
  cpuLoading: false,
  cpuError: null,
  cpuDiscontinuedLoaded: false,

  // GPU Data
  gpus: [],
  gpuLoading: false,
  gpuError: null,
  gpuDiscontinuedLoaded: false,

  // Motherboard Data
  motherboards: [],
  motherboardLoading: false,
  motherboardError: null,
  motherboardDiscontinuedLoaded: false,

  // Memory Data
  memory: [],
  memoryLoading: false,
  memoryError: null,
  memoryDiscontinuedLoaded: false,

  // Storage Data
  storage: [],
  storageLoading: false,
  storageError: null,
  storageDiscontinuedLoaded: false,

  // PSU Data
  psus: [],
  psuLoading: false,
  psuError: null,
  psuDiscontinuedLoaded: false,

  // Case Data
  cases: [],
  caseLoading: false,
  caseError: null,
  caseDiscontinuedLoaded: false,

  // Cooler Data
  coolers: [],
  coolerLoading: false,
  coolerError: null,
  coolerDiscontinuedLoaded: false,

  // Cache
  lastUpdated: null
}

/**
 * Component data reducer
 */
function componentDataReducer(state: ComponentDataState, action: ComponentDataAction): ComponentDataState {
  switch (action.type) {
    // CPU Cases
    case 'SET_CPU_LOADING':
      return { ...state, cpuLoading: action.payload }
    case 'SET_CPU_ERROR':
      return { ...state, cpuError: action.payload, cpuLoading: false }
    case 'SET_CPUS':
      return { ...state, cpus: action.payload, cpuLoading: false, cpuError: null, lastUpdated: Date.now() }
    case 'ADD_CPU':
      return { ...state, cpus: [...state.cpus, action.payload] }
    case 'UPDATE_CPU':
      return {
        ...state,
        cpus: state.cpus.map(cpu => cpu.id === action.payload.id ? action.payload : cpu)
      }
    case 'REMOVE_CPU':
      return {
        ...state,
        cpus: state.cpus.filter(cpu => cpu.id !== action.payload)
      }

    // GPU Cases
    case 'SET_GPU_LOADING':
      return { ...state, gpuLoading: action.payload }
    case 'SET_GPU_ERROR':
      return { ...state, gpuError: action.payload, gpuLoading: false }
    case 'SET_GPUS':
      return { ...state, gpus: action.payload, gpuLoading: false, gpuError: null, lastUpdated: Date.now() }
    case 'ADD_GPU':
      return { ...state, gpus: [...state.gpus, action.payload] }
    case 'UPDATE_GPU':
      return {
        ...state,
        gpus: state.gpus.map(gpu => gpu.id === action.payload.id ? action.payload : gpu)
      }
    case 'REMOVE_GPU':
      return {
        ...state,
        gpus: state.gpus.filter(gpu => gpu.id !== action.payload)
      }

    // Motherboard Cases
    case 'SET_MOTHERBOARD_LOADING':
      return { ...state, motherboardLoading: action.payload }
    case 'SET_MOTHERBOARD_ERROR':
      return { ...state, motherboardError: action.payload, motherboardLoading: false }
    case 'SET_MOTHERBOARDS':
      return { ...state, motherboards: action.payload, motherboardLoading: false, motherboardError: null, lastUpdated: Date.now() }
    case 'ADD_MOTHERBOARD':
      return { ...state, motherboards: [...state.motherboards, action.payload] }
    case 'UPDATE_MOTHERBOARD':
      return {
        ...state,
        motherboards: state.motherboards.map(mb => mb.id === action.payload.id ? action.payload : mb)
      }
    case 'REMOVE_MOTHERBOARD':
      return {
        ...state,
        motherboards: state.motherboards.filter(mb => mb.id !== action.payload)
      }

    // Memory Cases
    case 'SET_MEMORY_LOADING':
      return { ...state, memoryLoading: action.payload }
    case 'SET_MEMORY_ERROR':
      return { ...state, memoryError: action.payload, memoryLoading: false }
    case 'SET_MEMORY':
      return { ...state, memory: action.payload, memoryLoading: false, memoryError: null, lastUpdated: Date.now() }
    case 'ADD_MEMORY':
      return { ...state, memory: [...state.memory, action.payload] }
    case 'UPDATE_MEMORY':
      return {
        ...state,
        memory: state.memory.map(mem => mem.id === action.payload.id ? action.payload : mem)
      }
    case 'REMOVE_MEMORY':
      return {
        ...state,
        memory: state.memory.filter(mem => mem.id !== action.payload)
      }

    // Storage Cases
    case 'SET_STORAGE_LOADING':
      return { ...state, storageLoading: action.payload }
    case 'SET_STORAGE_ERROR':
      return { ...state, storageError: action.payload, storageLoading: false }
    case 'SET_STORAGE':
      return { ...state, storage: action.payload, storageLoading: false, storageError: null, lastUpdated: Date.now() }
    case 'ADD_STORAGE':
      return { ...state, storage: [...state.storage, action.payload] }
    case 'UPDATE_STORAGE':
      return {
        ...state,
        storage: state.storage.map(stor => stor.id === action.payload.id ? action.payload : stor)
      }
    case 'REMOVE_STORAGE':
      return {
        ...state,
        storage: state.storage.filter(stor => stor.id !== action.payload)
      }

    // PSU Cases
    case 'SET_PSU_LOADING':
      return { ...state, psuLoading: action.payload }
    case 'SET_PSU_ERROR':
      return { ...state, psuError: action.payload, psuLoading: false }
    case 'SET_PSUS':
      return { ...state, psus: action.payload, psuLoading: false, psuError: null, lastUpdated: Date.now() }
    case 'ADD_PSU':
      return { ...state, psus: [...state.psus, action.payload] }
    case 'UPDATE_PSU':
      return {
        ...state,
        psus: state.psus.map(psu => psu.id === action.payload.id ? action.payload : psu)
      }
    case 'REMOVE_PSU':
      return {
        ...state,
        psus: state.psus.filter(psu => psu.id !== action.payload)
      }

    // Case Cases
    case 'SET_CASE_LOADING':
      return { ...state, caseLoading: action.payload }
    case 'SET_CASE_ERROR':
      return { ...state, caseError: action.payload, caseLoading: false }
    case 'SET_CASES':
      return { ...state, cases: action.payload, caseLoading: false, caseError: null, lastUpdated: Date.now() }
    case 'ADD_CASE':
      return { ...state, cases: [...state.cases, action.payload] }
    case 'UPDATE_CASE':
      return {
        ...state,
        cases: state.cases.map(pcCase => pcCase.id === action.payload.id ? action.payload : pcCase)
      }
    case 'REMOVE_CASE':
      return {
        ...state,
        cases: state.cases.filter(pcCase => pcCase.id !== action.payload)
      }

    // Cooler Cases
    case 'SET_COOLER_LOADING':
      return { ...state, coolerLoading: action.payload }
    case 'SET_COOLER_ERROR':
      return { ...state, coolerError: action.payload, coolerLoading: false }
    case 'SET_COOLERS':
      return { ...state, coolers: action.payload, coolerLoading: false, coolerError: null, lastUpdated: Date.now() }
    case 'ADD_COOLER':
      return { ...state, coolers: [...state.coolers, action.payload] }
    case 'UPDATE_COOLER':
      return {
        ...state,
        coolers: state.coolers.map(cooler => cooler.id === action.payload.id ? action.payload : cooler)
      }
    case 'REMOVE_COOLER':
      return {
        ...state,
        coolers: state.coolers.filter(cooler => cooler.id !== action.payload)
      }

    // Cache Cases
    case 'UPDATE_CACHE_TIMESTAMP':
      return { ...state, lastUpdated: Date.now() }

    default:
      return state
  }
}

/**
 * Component data context interface
 */
interface ComponentDataContextType {
  state: ComponentDataState
  dispatch: React.Dispatch<ComponentDataAction>

  // Utility functions
  loadAllComponents: () => Promise<void>
  refreshComponent: (componentType: string, status?: 'Current' | 'Discontinued' | 'all') => Promise<void>
  getComponentData: (componentType: string) => unknown[]
  isComponentLoading: (componentType: string) => boolean
  loadDiscontinuedItems: (componentType: string) => Promise<void>
}

/**
 * Component data context
 */
const ComponentDataContext = createContext<ComponentDataContextType | undefined>(undefined)

/**
 * Component data provider component
 */
export function ComponentDataProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(componentDataReducer, initialComponentDataState)

  /**
   * Load all component data (legacy support)
   */
  const loadAllComponents = async () => {
    const componentTypes = ['cpu', 'gpu', 'motherboard', 'memory', 'storage', 'psu', 'case', 'cooler']

    await Promise.all(
      componentTypes.map(type => refreshComponent(type))
    )
  }

  /**
   * Load components with priority and status filtering
   * Priority: CPU, GPU, Motherboard (high priority)
   * Others: Memory, Storage, PSU, Case, Cooler (loaded in background)
   * Status: Current items only initially, Discontinued loaded on demand
   */
  const loadComponentsOptimized = async () => {
    // High priority components - load Current items first
    const highPriority = ['cpu', 'gpu', 'motherboard']
    const lowPriority = ['memory', 'storage', 'psu', 'case', 'cooler']

    // Load high priority Current items first
    await Promise.all(
      highPriority.map(type => refreshComponent(type, 'Current'))
    )

    // Load low priority Current items in background
    Promise.all(
      lowPriority.map(type => refreshComponent(type, 'Current'))
    ).catch(error => {
      console.error('Failed to load low priority components:', error)
    })
  }

  /**
   * Refresh specific component data with optional status filter
   * @param componentType - Type of component to load
   * @param status - Optional status filter ('Current' or 'Discontinued' or 'all')
   */
  const refreshComponent = async (
    componentType: string,
    status: 'Current' | 'Discontinued' | 'all' = 'all'
  ) => {
    const dynamicDispatch = dispatch as React.Dispatch<{ type: string; payload?: unknown }>
    try {
      // Check if Discontinued is already loaded
      if (status === 'Discontinued') {
        const discontinuedLoadedKey = `${componentType}DiscontinuedLoaded` as keyof ComponentDataState
        if (state[discontinuedLoadedKey]) {
          // Already loaded, skip
          return
        }
      }

      // Only set loading state when loading initial data (Current or all)
      // Don't set loading when merging Discontinued items to prevent white flash
      if (status !== 'Discontinued') {
        dynamicDispatch({ type: `SET_${componentType.toUpperCase()}_LOADING`, payload: true })
      }

      // Use optimized adapters for storage, CPU, and motherboard components
      let data
      if (componentType.toLowerCase() === 'storage') {
        const storageAdapter = getStorageDataAdapter()

        if (status === 'Current') {
          data = await storageAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await storageAdapter.getDiscontinuedItems()
        } else {
          data = await storageAdapter.getAllItems()
        }
      } else if (componentType.toLowerCase() === 'cpu') {
        const cpuAdapter = getCPUDataAdapter()

        if (status === 'Current') {
          data = await cpuAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await cpuAdapter.getDiscontinuedItems()
        } else {
          data = await cpuAdapter.getAllItems()
        }
      } else if (componentType.toLowerCase() === 'motherboard') {
        const motherboardAdapter = getMotherboardDataAdapter()

        if (status === 'Current') {
          data = await motherboardAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await motherboardAdapter.getDiscontinuedItems()
        } else {
          data = await motherboardAdapter.getAllItems()
        }
      } else if (componentType.toLowerCase() === 'case') {
        const caseAdapter = getCaseDataAdapter()

        if (status === 'Current') {
          data = await caseAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await caseAdapter.getDiscontinuedItems()
        } else {
          data = await caseAdapter.getAllItems()
        }
      } else if (componentType.toLowerCase() === 'gpu') {
        const gpuAdapter = getGPUDataAdapter()

        if (status === 'Current') {
          data = await gpuAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await gpuAdapter.getDiscontinuedItems()
        } else {
          data = await gpuAdapter.getAllItems()
        }
      } else if (componentType.toLowerCase() === 'memory') {
        const memoryAdapter = getMemoryDataAdapter()

        if (status === 'Current') {
          data = await memoryAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await memoryAdapter.getDiscontinuedItems()
        } else {
          data = await memoryAdapter.getAllItems()
        }
      } else if (componentType.toLowerCase() === 'psu') {
        const psuAdapter = getPSUDataAdapter()

        if (status === 'Current') {
          data = await psuAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await psuAdapter.getDiscontinuedItems()
        } else {
          data = await psuAdapter.getAllItems()
        }
      } else if (componentType.toLowerCase() === 'cooler') {
        const coolerAdapter = getCoolerDataAdapter()

        if (status === 'Current') {
          data = await coolerAdapter.getCurrentItems()
        } else if (status === 'Discontinued') {
          data = await coolerAdapter.getDiscontinuedItems()
        } else {
          data = await coolerAdapter.getAllItems()
        }
      } else {
        throw new Error(`Unknown component type: ${componentType}`)
      }

      // Map component types to their correct action names
      const actionMapping: Record<string, string> = {
        cpu: 'SET_CPUS',
        gpu: 'SET_GPUS',
        motherboard: 'SET_MOTHERBOARDS',
        memory: 'SET_MEMORY',
        storage: 'SET_STORAGE',
        psu: 'SET_PSUS',
        case: 'SET_CASES',
        cooler: 'SET_COOLERS'
      }

      const actionType = actionMapping[componentType.toLowerCase()] || `SET_${componentType.toUpperCase()}S`

      // If loading Discontinued, merge with existing Current data
      if (status === 'Discontinued') {
        const existingData = getComponentData(componentType)
        // Deduplicate by ID to prevent double loading
        const existingIds = new Set(existingData.map((item: { id: string }) => item.id))
        const newData = data.filter((item: { id: string }) => !existingIds.has(item.id))
        const mergedData = [...existingData, ...newData]
        dynamicDispatch({ type: actionType, payload: mergedData })

        // Mark Discontinued as loaded
        const discontinuedLoadedKey = `${componentType}DiscontinuedLoaded` as keyof ComponentDataState
        ;(state as unknown as Record<string, unknown>)[discontinuedLoadedKey] = true
      } else {
        dynamicDispatch({ type: actionType, payload: data })
      }
    } catch (error) {
      dynamicDispatch({
        type: `SET_${componentType.toUpperCase()}_ERROR`,
        payload: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  /**
   * Load Discontinued items for a specific component type
   * Used for lazy loading when user selects Discontinued filter
   */
  const loadDiscontinuedItems = async (componentType: string) => {
    await refreshComponent(componentType, 'Discontinued')
  }

  /**
   * Get component data by type
   */
  const getComponentData = (componentType: string) => {
    switch (componentType.toLowerCase()) {
      case 'cpu': return state.cpus
      case 'gpu': return state.gpus
      case 'motherboard': return state.motherboards
      case 'memory': return state.memory
      case 'storage': return state.storage
      case 'psu': return state.psus
      case 'case': return state.cases
      case 'cooler': return state.coolers
      default: return []
    }
  }

  /**
   * Check if component is loading
   */
  const isComponentLoading = (componentType: string) => {
    switch (componentType.toLowerCase()) {
      case 'cpu': return state.cpuLoading
      case 'gpu': return state.gpuLoading
      case 'motherboard': return state.motherboardLoading
      case 'memory': return state.memoryLoading
      case 'storage': return state.storageLoading
      case 'psu': return state.psuLoading
      case 'case': return state.caseLoading
      case 'cooler': return state.coolerLoading
      default: return false
    }
  }

  // Load data on mount with optimized strategy
  useEffect(() => {
    loadComponentsOptimized().catch(error => {
      console.error('Failed to load components on mount:', error)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue: ComponentDataContextType = {
    state,
    dispatch,
    loadAllComponents,
    refreshComponent,
    getComponentData,
    isComponentLoading,
    loadDiscontinuedItems
  }

  return (
    <ComponentDataContext.Provider value={contextValue}>
      {children}
    </ComponentDataContext.Provider>
  )
}

/**
 * Hook to use component data context
 */
export function useComponentData() {
  const context = useContext(ComponentDataContext)
  if (context === undefined) {
    throw new Error('useComponentData must be used within a ComponentDataProvider')
  }
  return context
}